import { useState, useRef, useLayoutEffect, RefObject } from "react";

interface UseTimelinePanProps {
  containerRef: RefObject<HTMLDivElement>;
  timeAxisRef: RefObject<HTMLDivElement>;
  zoom: number;
}

export const useTimelinePan = ({ containerRef, timeAxisRef, zoom }: UseTimelinePanProps) => {
  const [scrollRatio, setScrollRatio] = useState(0);
  const syncingScrollRef = useRef<boolean>(false);
  const scrollSyncRafRef = useRef<number | null>(null);
  const pendingScrollLeftRef = useRef<number | null>(null);

  /**
   * Handle scroll event with RAF optimization
   */
  const handleScroll = () => {
    const container = containerRef.current;
    const timeAxis = timeAxisRef.current;
    if (!container || !timeAxis) return;

    // Batch updates in RAF
    pendingScrollLeftRef.current = container.scrollLeft;

    if (scrollSyncRafRef.current === null) {
      scrollSyncRafRef.current = requestAnimationFrame(() => {
        const left = pendingScrollLeftRef.current ?? container.scrollLeft;
        pendingScrollLeftRef.current = null;
        scrollSyncRafRef.current = null;

        const newRatio =
          container.scrollWidth > container.clientWidth
            ? left / (container.scrollWidth - container.clientWidth)
            : 0;

        if (newRatio !== scrollRatio) {
          setScrollRatio(isNaN(newRatio) ? 0 : newRatio);
        }

        if (!syncingScrollRef.current && timeAxis.scrollLeft !== left) {
          syncingScrollRef.current = true;
          timeAxis.scrollLeft = left;
          requestAnimationFrame(() => {
            syncingScrollRef.current = false;
          });
        }
      });
    }
  };

  /**
   * Scroll to a specific position
   */
  const scrollTo = (scrollLeft: number) => {
    const container = containerRef.current;
    const timeAxis = timeAxisRef.current;
    if (!container) return;

    container.scrollLeft = scrollLeft;
    if (timeAxis) {
      timeAxis.scrollLeft = scrollLeft;
    }
  };

  /**
   * Center on a specific x-coordinate
   */
  const centerOn = (x: number) => {
    const container = containerRef.current;
    if (!container) return;

    const scrollLeft = Math.max(0, x - container.clientWidth / 2);
    scrollTo(scrollLeft);
  };

  /**
   * Sync scroll position when zoom changes.
   *
   * Layout, not passive: the restored `scrollLeft` has to be in place before the
   * frame is painted, so that whoever culls to the visible window
   * (`useVisibleXRange`, declared after this hook and therefore run after it)
   * reads the position the zoom actually lands on rather than the pre-zoom one.
   */
  useLayoutEffect(() => {
    const container = containerRef.current;
    const timeAxis = timeAxisRef.current;
    if (!container) return;

    const maxScroll = container.scrollWidth - container.clientWidth;
    const desired = scrollRatio * maxScroll;

    if (Math.abs(container.scrollLeft - desired) > 1) {
      container.scrollLeft = desired;
      if (timeAxis) {
        timeAxis.scrollLeft = desired;
      }
    }
  }, [zoom, scrollRatio]);

  return {
    scrollRatio,
    setScrollRatio,
    handleScroll,
    scrollTo,
    centerOn,
  };
};
